import { Phone, MessageCircle, Hexagon, CalendarCheck, ShoppingBag, Briefcase, Smartphone } from "lucide-react";
import { MockSystem } from "@/components/demo/mockSystem";

export const TEMPLATES = [
  {
    id: "consultorio",
    name: "Consultorio / Salud",
    desc: "Médicos, dentistas, terapeutas, veterinarios",
    primary: "#0E7C7B",
    accent: "#14B8A6",
    cta: "Agendar cita",
    servicesLabel: "Nuestros servicios",
    defaultServices: [
      { title: "Consulta general", description: "Atención personalizada con agenda en línea." },
      { title: "Especialidades", description: "Profesionales por área de salud." },
      { title: "Citas en línea", description: "Reserva y recordatorios automáticos." },
    ],
  },
  {
    id: "tienda",
    name: "Tienda / Comercial",
    desc: "Emprendimientos, manualidades, florerías, catálogos",
    primary: "#C2410C",
    accent: "#F59E0B",
    cta: "Hacer pedido",
    servicesLabel: "Nuestros productos",
    defaultServices: [
      { title: "Catálogo digital", description: "Productos organizados y siempre actualizados." },
      { title: "Pedidos por WhatsApp", description: "El cliente pide directo desde la página." },
      { title: "Entregas a domicilio", description: "Coordinación de envíos en la ciudad." },
    ],
  },
  {
    id: "servicios",
    name: "Empresa de Servicios",
    desc: "Consultorías, talleres, agencias, profesionales",
    primary: "#1D4ED8",
    accent: "#00F0FF",
    cta: "Solicitar cotización",
    servicesLabel: "Lo que ofrecemos",
    defaultServices: [
      { title: "Servicio principal", description: "Descripción del servicio estrella del negocio." },
      { title: "Atención personalizada", description: "Acompañamiento en cada etapa." },
      { title: "Soporte continuo", description: "Respuesta rápida cuando lo necesites." },
    ],
  },
  {
    id: "ganadero",
    name: "Sector Ganadero",
    desc: "Haciendas, fincas, ganaderías y producción",
    primary: "#166534",
    accent: "#A3E635",
    cta: "Solicitar información",
    servicesLabel: "Soluciones para tu hacienda",
    defaultServices: [
      { title: "Control de lotes y animales", description: "Cada animal y potrero registrado y ubicado." },
      { title: "Registro sanitario", description: "Vacunas y tratamientos con alertas de próxima dosis." },
      { title: "Pesos y ventas", description: "Producción medida y ventas negociadas desde el sistema." },
    ],
  },
  {
    id: "turismo",
    name: "Agencia de Viajes",
    desc: "Turismo, tours y viajes con cronograma",
    primary: "#0369A1",
    accent: "#F97316",
    cta: "Reservar viaje",
    servicesLabel: "Lo que tu agencia puede hacer",
    defaultServices: [
      { title: "Viajes con cronograma", description: "Itinerarios día por día, organizados y visibles." },
      { title: "Reservas y pagos en línea", description: "Cupos confirmados con pago automático." },
      { title: "App del viajero", description: "El cliente sigue su viaje desde su celular." },
    ],
  },
];

const waLink = (d) => {
  const num = (d.whatsapp || "").replace(/\D/g, "");
  if (!num) return null;
  return `https://wa.me/${num}?text=${encodeURIComponent(`Hola ${d.business_name}, vi su página y quiero más información.`)}`;
};

const HeroArt = ({ d, className = "" }) =>
  d.hero_image ? (
    <img src={d.hero_image} alt={d.business_name} className={`object-cover ${className}`} />
  ) : (
    <div
      className={`grid place-items-center ${className}`}
      style={{ background: `linear-gradient(135deg, ${d.primary_color}, ${d.accent_color})` }}
    >
      <span className="font-display text-7xl font-black text-white/80">{(d.business_name || "Q")[0].toUpperCase()}</span>
    </div>
  );

export const QCBadge = () => (
  <a
    href="/"
    data-testid="demo-qc-badge"
    className="fixed bottom-4 left-4 z-50 flex items-center gap-2 rounded-full bg-ink/90 px-4 py-2 font-mono text-[11px] text-cy shadow-xl backdrop-blur transition-colors hover:text-em"
  >
    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-em" />
    Demo creada por Quantum Century
  </a>
);

const ServiceCards = ({ d, light = true }) => (
  <div className="mx-auto grid max-w-5xl gap-5 px-6 sm:grid-cols-3">
    {(d.services || []).filter((s) => s.title).map((s, i) => (
      <div key={i} className={`rounded-xl border p-6 ${light ? "border-slate-200 bg-white" : "border-white/10 bg-white/5"}`}>
        <span className="mb-4 block h-1.5 w-10 rounded-full" style={{ backgroundColor: d.accent_color }} />
        <h3 className={`font-display text-lg font-bold ${light ? "text-slate-900" : "text-white"}`}>{s.title}</h3>
        {s.description && <p className={`mt-2 text-sm leading-relaxed ${light ? "text-slate-500" : "text-slate-300"}`}>{s.description}</p>}
      </div>
    ))}
  </div>
);

const FooterBar = ({ d, dark = true }) => {
  const wa = waLink(d);
  return (
    <footer className={`py-10 text-center ${dark ? "text-white" : "text-slate-700"}`} style={dark ? { backgroundColor: d.primary_color } : {}}>
      <p className="font-display text-xl font-bold">{d.business_name}</p>
      <div className={`mt-3 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm ${dark ? "text-white/80" : "text-slate-500"}`}>
        {d.phone && (
          <span className="flex items-center gap-1.5">
            <Phone className="h-3.5 w-3.5" /> {d.phone}
          </span>
        )}
        {wa && (
          <a href={wa} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 underline-offset-2 hover:underline">
            <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
          </a>
        )}
        {d.email && <span>{d.email}</span>}
      </div>
    </footer>
  );
};

const Consultorio = ({ d }) => {
  const wa = waLink(d);
  return (
    <div className="min-h-screen bg-[#F8FAFA] font-body text-slate-800">
      <header className="sticky top-0 z-40 border-b border-slate-100 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <span className="font-display text-lg font-extrabold" style={{ color: d.primary_color }}>{d.business_name}</span>
          {wa && (
            <a href={wa} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold text-white" style={{ backgroundColor: d.primary_color }}>
              <CalendarCheck className="h-4 w-4" /> Agendar cita
            </a>
          )}
        </div>
      </header>
      <section className="mx-auto grid max-w-6xl items-center gap-10 px-6 py-16 sm:py-24 lg:grid-cols-2">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.25em]" style={{ color: d.accent_color }}>Bienvenido</p>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight text-slate-900 sm:text-5xl">{d.tagline || d.business_name}</h1>
          <p className="mt-5 max-w-lg leading-relaxed text-slate-500">{d.description}</p>
          {wa && (
            <a href={wa} target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-display font-bold text-white shadow-lg" style={{ backgroundColor: d.primary_color }}>
              <MessageCircle className="h-5 w-5" /> Agenda tu cita por WhatsApp
            </a>
          )}
        </div>
        <HeroArt d={d} className="h-72 w-full rounded-2xl shadow-xl sm:h-96" />
      </section>
      <section className="py-16" style={{ backgroundColor: `${d.primary_color}0D` }}>
        <h2 className="mb-10 text-center font-display text-3xl font-bold text-slate-900">Nuestros servicios</h2>
        <ServiceCards d={d} />
      </section>
      {d.about_image && (
        <section className="mx-auto grid max-w-6xl items-center gap-10 px-6 py-16 lg:grid-cols-2">
          <img src={d.about_image} alt={d.business_name} className="h-72 w-full rounded-2xl object-cover shadow-lg" />
          <p className="leading-relaxed text-slate-600">{d.description}</p>
        </section>
      )}
      {(d.show_erp || d.show_crm) && <MockSystem d={d} />}
      <FooterBar d={d} />
      <QCBadge />
    </div>
  );
};

const Tienda = ({ d }) => {
  const wa = waLink(d);
  return (
    <div className="min-h-screen bg-[#FFFBF7] font-body text-slate-800">
      <header className="sticky top-0 z-40 border-b border-orange-100 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <span className="font-display text-lg font-extrabold" style={{ color: d.primary_color }}>{d.business_name}</span>
          {d.phone && <span className="flex items-center gap-1.5 text-sm text-slate-500"><Phone className="h-4 w-4" />{d.phone}</span>}
        </div>
      </header>
      <section className="relative overflow-hidden py-20 text-center sm:py-28">
        <div className="pointer-events-none absolute inset-0 opacity-10" style={{ background: `radial-gradient(circle at 50% 0%, ${d.accent_color}, transparent 60%)` }} />
        <div className="relative mx-auto max-w-3xl px-6">
          <p className="font-mono text-xs uppercase tracking-[0.3em]" style={{ color: d.accent_color }}>Tienda en línea</p>
          <h1 className="mt-4 font-display text-5xl font-black leading-tight text-slate-900 sm:text-6xl">{d.business_name}</h1>
          <p className="mx-auto mt-5 max-w-xl leading-relaxed text-slate-500">{d.tagline || d.description}</p>
          {wa && (
            <a href={wa} target="_blank" rel="noopener noreferrer" className="mt-9 inline-flex items-center gap-2 rounded-full px-8 py-4 font-display font-bold text-white shadow-xl" style={{ backgroundColor: d.primary_color }}>
              <ShoppingBag className="h-5 w-5" /> Hacer pedido por WhatsApp
            </a>
          )}
        </div>
      </section>
      {d.hero_image && (
        <div className="mx-auto max-w-5xl px-6 pb-4">
          <img src={d.hero_image} alt={d.business_name} className="h-80 w-full rounded-2xl object-cover shadow-xl" />
        </div>
      )}
      <section className="py-16">
        <h2 className="mb-10 text-center font-display text-3xl font-bold text-slate-900">Nuestros productos</h2>
        <ServiceCards d={d} />
      </section>
      {(d.show_erp || d.show_crm) && <MockSystem d={d} />}
      <FooterBar d={d} />
      <QCBadge />
    </div>
  );
};

const Servicios = ({ d }) => {
  const wa = waLink(d);
  return (
    <div className="min-h-screen bg-white font-body text-slate-800">
      <section className="relative overflow-hidden" style={{ backgroundColor: d.primary_color }}>
        <div className="pointer-events-none absolute inset-0 opacity-20" style={{ background: `radial-gradient(circle at 80% 20%, ${d.accent_color}, transparent 55%)` }} />
        <header className="relative mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
          <span className="font-display text-lg font-extrabold text-white">{d.business_name}</span>
          {wa && (
            <a href={wa} target="_blank" rel="noopener noreferrer" className="rounded-full bg-white/15 px-5 py-2.5 text-sm font-bold text-white backdrop-blur transition-colors hover:bg-white/25">
              Contactar
            </a>
          )}
        </header>
        <div className="relative mx-auto max-w-6xl px-6 pb-20 pt-14 sm:pb-28 sm:pt-20">
          <p className="font-mono text-xs uppercase tracking-[0.3em]" style={{ color: d.accent_color }}>Servicios profesionales</p>
          <h1 className="mt-4 max-w-2xl font-display text-4xl font-extrabold leading-tight text-white sm:text-5xl">{d.tagline || d.business_name}</h1>
          <p className="mt-5 max-w-xl leading-relaxed text-white/75">{d.description}</p>
          {wa && (
            <a href={wa} target="_blank" rel="noopener noreferrer" className="mt-9 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 font-display font-bold shadow-xl" style={{ color: d.primary_color }}>
              <Briefcase className="h-5 w-5" /> Solicitar cotización
            </a>
          )}
        </div>
      </section>
      {d.hero_image && (
        <div className="mx-auto -mt-10 max-w-5xl px-6">
          <img src={d.hero_image} alt={d.business_name} className="h-72 w-full rounded-2xl object-cover shadow-2xl" />
        </div>
      )}
      <section className="py-16 sm:py-20">
        <h2 className="mb-10 text-center font-display text-3xl font-bold text-slate-900">Lo que ofrecemos</h2>
        <ServiceCards d={d} />
      </section>
      {(d.show_erp || d.show_crm) && <MockSystem d={d} />}
      <FooterBar d={d} />
      <QCBadge />
    </div>
  );
};

const Ganadero = ({ d }) => {
  const wa = waLink(d);
  return (
    <div className="min-h-screen bg-[#F7FAF5] font-body text-slate-800">
      <header className="sticky top-0 z-40 border-b border-green-100 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <span className="font-display text-lg font-extrabold" style={{ color: d.primary_color }}>{d.business_name}</span>
          {wa && (
            <a href={wa} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold text-white" style={{ backgroundColor: d.primary_color }}>
              <MessageCircle className="h-4 w-4" /> Solicitar información
            </a>
          )}
        </div>
      </header>
      <section className="mx-auto grid max-w-6xl items-center gap-10 px-6 py-16 sm:py-24 lg:grid-cols-2">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.25em]" style={{ color: d.accent_color }}>Gestión ganadera</p>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight text-slate-900 sm:text-5xl">{d.tagline || d.business_name}</h1>
          <p className="mt-5 max-w-lg leading-relaxed text-slate-500">{d.description}</p>
          {wa && (
            <a href={wa} target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-display font-bold text-white shadow-lg" style={{ backgroundColor: d.primary_color }}>
              <MessageCircle className="h-5 w-5" /> Escríbenos por WhatsApp
            </a>
          )}
        </div>
        <HeroArt d={d} className="h-72 w-full rounded-2xl shadow-xl sm:h-96" />
      </section>
      <section className="py-16" style={{ backgroundColor: `${d.primary_color}0D` }}>
        <h2 className="mb-10 text-center font-display text-3xl font-bold text-slate-900">Soluciones para tu hacienda</h2>
        <ServiceCards d={d} />
      </section>
      {d.about_image && (
        <section className="mx-auto grid max-w-6xl items-center gap-10 px-6 py-16 lg:grid-cols-2">
          <img src={d.about_image} alt={d.business_name} className="h-72 w-full rounded-2xl object-cover shadow-lg" />
          <p className="leading-relaxed text-slate-600">{d.description}</p>
        </section>
      )}
      {(d.show_erp || d.show_crm) && <MockSystem d={d} />}
      <FooterBar d={d} />
      <QCBadge />
    </div>
  );
};

const Turismo = ({ d }) => {
  const wa = waLink(d);
  return (
    <div className="min-h-screen bg-white font-body text-slate-800">
      <section className="relative overflow-hidden" style={{ background: `linear-gradient(160deg, ${d.primary_color}, #082f49)` }}>
        <header className="relative mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
          <span className="font-display text-lg font-extrabold text-white">{d.business_name}</span>
          {wa && (
            <a href={wa} target="_blank" rel="noopener noreferrer" className="rounded-full bg-white/15 px-5 py-2.5 text-sm font-bold text-white backdrop-blur transition-colors hover:bg-white/25">
              Reservar
            </a>
          )}
        </header>
        <div className="relative mx-auto max-w-3xl px-6 pb-20 pt-12 text-center sm:pb-28 sm:pt-16">
          <p className="font-mono text-xs uppercase tracking-[0.3em]" style={{ color: d.accent_color }}>Agencia de viajes</p>
          <h1 className="mt-4 font-display text-4xl font-extrabold leading-tight text-white sm:text-6xl">{d.business_name}</h1>
          <p className="mx-auto mt-5 max-w-xl leading-relaxed text-white/75">{d.tagline || d.description}</p>
          <p className="mx-auto mt-6 inline-flex items-center gap-2 rounded-full bg-white/10 px-5 py-2.5 text-sm text-white/90 backdrop-blur">
            <Smartphone className="h-4 w-4" style={{ color: d.accent_color }} />
            Tus clientes siguen su viaje, día por día, desde su celular
          </p>
          {wa && (
            <div className="mt-8">
              <a href={wa} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 font-display font-bold shadow-xl" style={{ color: d.primary_color }}>
                <MessageCircle className="h-5 w-5" /> Reservar por WhatsApp
              </a>
            </div>
          )}
        </div>
      </section>
      {d.hero_image && (
        <div className="mx-auto -mt-12 max-w-5xl px-6">
          <img src={d.hero_image} alt={d.business_name} className="h-72 w-full rounded-2xl object-cover shadow-2xl" />
        </div>
      )}
      <section className="py-16 sm:py-20">
        <h2 className="mb-10 text-center font-display text-3xl font-bold text-slate-900">Lo que tu agencia puede hacer</h2>
        <ServiceCards d={d} />
      </section>
      {(d.show_erp || d.show_crm) && <MockSystem d={d} />}
      <FooterBar d={d} />
      <QCBadge />
    </div>
  );
};

export const DemoRenderer = ({ demo }) => {
  if (demo.template === "consultorio") return <Consultorio d={demo} />;
  if (demo.template === "tienda") return <Tienda d={demo} />;
  if (demo.template === "ganadero") return <Ganadero d={demo} />;
  if (demo.template === "turismo") return <Turismo d={demo} />;
  return <Servicios d={demo} />;
};

export const templateById = (id) => TEMPLATES.find((t) => t.id === id) || TEMPLATES[0];
